import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Autocomplete,
  Chip,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput
} from '@mui/material';
import api from '../services/api';

const FeasibilityAnalysisForm = ({ open, onClose, onSubmit, initialData, projectId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [technicalResponsibles, setTechnicalResponsibles] = useState([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  
  const [formData, setFormData] = useState({
    // Setor Solicitante/Beneficiado
    requesting_department: '',
    requester_name: '',
    
    // Escopo do Projeto
    problem_description: '',
    project_objectives: '',
    
    // Viabilidade Técnica
    is_technically_feasible: true,
    technologies_used: '',
    technical_limitations: '',
    requires_integrations: false,
    integration_systems: '',
    
    // Necessidade e Impacto
    problem_solved: '',
    benefited_sectors: '',
    workflow_impact: '',
    has_simpler_alternative: false,
    alternative_description: '',
    
    // Análise de Tempo e Recursos
    development_days: 0,
    testing_days: 0,
    documentation_days: 0,
    deployment_days: 0,
    training_days: 0,
    required_team_members: 1,
    requires_ongoing_support: false,
    
    // Análise de Custo e Orçamento
    development_cost: 0,
    infrastructure_cost: 0,
    training_documentation_cost: 0,
    support_maintenance_cost: 0,
    
    // Riscos e Mitigações
    project_risks: '',
    risk_mitigation_strategies: '',
    contingency_plan: '',
    
    // Priorização do Projeto
    aligns_with_company_goals: true,
    implementation_urgency: 'Média',
    has_significant_impact_if_delayed: false,
    
    // Resultado da Análise
    decision: 'Aprovado',
    decision_justification: '',
    
    // Datas, Prioridade e Responsáveis
    start_date: new Date().toISOString().split('T')[0],
    expected_end_date: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
    priority: 'Média',
    responsible_persons: '',
    estimated_budget: 0,
  });

  // Função para buscar os responsáveis técnicos
  const fetchTechnicalResponsibles = async () => {
    try {
      const response = await api.get('/technical-responsibles/');
      // Filtrar apenas responsáveis ativos
      const activeResponsibles = response.data.filter(resp => resp.is_active);
      setTechnicalResponsibles(activeResponsibles);
      console.log('Responsáveis técnicos carregados:', activeResponsibles);
    } catch (error) {
      console.error('Erro ao buscar responsáveis técnicos:', error);
    }
  };

  useEffect(() => {
    fetchTechnicalResponsibles();
  }, []);

  useEffect(() => {
    if (initialData && open) {
      setFormData(initialData);
      
      // Se houver responsible_persons como string, converter para array
      if (initialData.responsible_persons && typeof initialData.responsible_persons === 'string') {
        const respNames = initialData.responsible_persons.split(',').map(name => name.trim());
        setSelectedResponsibles(respNames.filter(name => name)); // Filtra strings vazias
      } else if (Array.isArray(initialData.responsible_persons)) {
        setSelectedResponsibles(initialData.responsible_persons.filter(name => name)); // Filtra strings vazias
      } else {
        setSelectedResponsibles([]);
      }
    } else {
      // Reset quando o formulário é fechado
      setSelectedResponsibles([]);
    }
  }, [initialData, open]);

  const steps = [
    'Setor Solicitante',
    'Escopo',
    'Viabilidade Técnica',
    'Necessidade e Impacto',
    'Tempo e Recursos',
    'Custo e Orçamento',
    'Riscos',
    'Priorização',
    'Decisão'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Função específica para lidar com a mudança de responsáveis
  const handleResponsiblesChange = (event) => {
    const {
      target: { value },
    } = event;
    
    // Garantir que value seja sempre tratado como array
    const selectedValues = typeof value === 'string' ? value.split(',') : value;
    setSelectedResponsibles(selectedValues);
    
    // Atualizar o formData com a string formatada
    setFormData(prev => ({
      ...prev,
      responsible_persons: selectedValues.length > 0 ? selectedValues.join(', ') : ''
    }));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Adicionar o project_id antes de enviar
      const dataToSubmit = {
        ...formData,
        project_id: projectId,
        // Garantir que responsible_persons seja sempre uma string, mesmo se estiver vazio
        responsible_persons: selectedResponsibles.length > 0 ? selectedResponsibles.join(', ') : ''
      };
      
      console.log('Enviando análise de viabilidade:', dataToSubmit);
      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          // Setor Solicitante/Beneficiado
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Setor Solicitante/Beneficiado</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome do setor/departamento"
                name="requesting_department"
                value={formData.requesting_department}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Responsável pela solicitação"
                name="requester_name"
                value={formData.requester_name}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          // Escopo do Projeto
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Escopo do Projeto</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Descrição do problema a ser resolvido"
                name="problem_description"
                value={formData.problem_description}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Objetivos do projeto"
                name="project_objectives"
                value={formData.project_objectives}
                onChange={handleChange}
                required
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          // Viabilidade Técnica
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Viabilidade Técnica</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">O desenvolvimento da solução é tecnicamente viável?</FormLabel>
                <RadioGroup
                  row
                  name="is_technically_feasible"
                  value={formData.is_technically_feasible.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    is_technically_feasible: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quais tecnologias serão utilizadas?"
                name="technologies_used"
                value={formData.technologies_used}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Existem limitações técnicas ou dependências?"
                name="technical_limitations"
                value={formData.technical_limitations}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Serão necessárias integrações com outros sistemas?</FormLabel>
                <RadioGroup
                  row
                  name="requires_integrations"
                  value={formData.requires_integrations.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    requires_integrations: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {formData.requires_integrations && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Quais sistemas precisarão de integração?"
                  name="integration_systems"
                  value={formData.integration_systems}
                  onChange={handleChange}
                />
              </Grid>
            )}
          </Grid>
        );

      case 3:
        return (
          // Necessidade e Impacto
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Necessidade e Impacto</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Qual problema será resolvido?"
                name="problem_solved"
                value={formData.problem_solved}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Quais setores e usuários serão beneficiados?"
                name="benefited_sectors"
                value={formData.benefited_sectors}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Como o software impactará os fluxos de trabalho atuais?"
                name="workflow_impact"
                value={formData.workflow_impact}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Existe uma alternativa mais simples ou uma solução pronta que atenda à necessidade?</FormLabel>
                <RadioGroup
                  row
                  name="has_simpler_alternative"
                  value={formData.has_simpler_alternative.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    has_simpler_alternative: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
            {formData.has_simpler_alternative && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Qual seria a alternativa?"
                  name="alternative_description"
                  value={formData.alternative_description}
                  onChange={handleChange}
                />
              </Grid>
            )}
          </Grid>
        );

      case 4:
        return (
          // Análise de Tempo e Recursos
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Análise de Tempo e Recursos</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Estimativa de tempo necessária para:</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Desenvolvimento (dias)"
                name="development_days"
                type="number"
                value={formData.development_days}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Testes (dias)"
                name="testing_days"
                type="number"
                value={formData.testing_days}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Documentação (dias)"
                name="documentation_days"
                type="number"
                value={formData.documentation_days}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Implantação (dias)"
                name="deployment_days"
                type="number"
                value={formData.deployment_days}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Treinamento dos usuários (dias)"
                name="training_days"
                type="number"
                value={formData.training_days}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Desenvolvedores e membros necessários"
                name="required_team_members"
                type="number"
                value={formData.required_team_members}
                onChange={handleChange}
                required
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Existe a necessidade de suporte contínuo ou manutenções frequentes?</FormLabel>
                <RadioGroup
                  row
                  name="requires_ongoing_support"
                  value={formData.requires_ongoing_support.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    requires_ongoing_support: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 5:
        return (
          // Análise de Custo e Orçamento
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Análise de Custo e Orçamento</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Custo estimado para:</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Desenvolvimento"
                name="development_cost"
                type="number"
                value={formData.development_cost}
                onChange={handleChange}
                required
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Infraestrutura"
                name="infrastructure_cost"
                type="number"
                value={formData.infrastructure_cost}
                onChange={handleChange}
                required
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Treinamento e documentação"
                name="training_documentation_cost"
                type="number"
                value={formData.training_documentation_cost}
                onChange={handleChange}
                required
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Suporte e manutenção"
                name="support_maintenance_cost"
                type="number"
                value={formData.support_maintenance_cost}
                onChange={handleChange}
                required
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle1" gutterBottom>Orçamento Total Estimado:</Typography>
                <Typography variant="h6" color="primary">
                  R$ {(
                    parseFloat(formData.development_cost || 0) +
                    parseFloat(formData.infrastructure_cost || 0) +
                    parseFloat(formData.training_documentation_cost || 0) +
                    parseFloat(formData.support_maintenance_cost || 0)
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        );

      case 6:
        return (
          // Riscos e Mitigações
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Riscos e Mitigações</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Principais riscos do projeto (tecnológicos, operacionais, financeiros)"
                name="project_risks"
                value={formData.project_risks}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Estratégias para mitigar esses riscos"
                name="risk_mitigation_strategies"
                value={formData.risk_mitigation_strategies}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plano de contingência caso o desenvolvimento não ocorra como esperado"
                name="contingency_plan"
                value={formData.contingency_plan}
                onChange={handleChange}
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        );

      case 7:
        return (
          // Priorização do Projeto
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Priorização do Projeto</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">O projeto se alinha aos objetivos estratégicos da empresa?</FormLabel>
                <RadioGroup
                  row
                  name="aligns_with_company_goals"
                  value={formData.aligns_with_company_goals.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    aligns_with_company_goals: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <FormLabel component="legend">Qual a urgência da implementação?</FormLabel>
                <RadioGroup
                  row
                  name="implementation_urgency"
                  value={formData.implementation_urgency}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Alta" control={<Radio />} label="Alta" />
                  <FormControlLabel value="Média" control={<Radio />} label="Média" />
                  <FormControlLabel value="Baixa" control={<Radio />} label="Baixa" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Existe impacto financeiro ou operacional significativo se o projeto for adiado?</FormLabel>
                <RadioGroup
                  row
                  name="has_significant_impact_if_delayed"
                  value={formData.has_significant_impact_if_delayed.toString()}
                  onChange={(e) => setFormData({
                    ...formData,
                    has_significant_impact_if_delayed: e.target.value === 'true'
                  })}
                >
                  <FormControlLabel value="true" control={<Radio />} label="Sim" />
                  <FormControlLabel value="false" control={<Radio />} label="Não" />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 8:
        return (
          // Decisão e Aprovação
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Decisão e Aprovação</Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Justificativa da Decisão"
                name="decision_justification"
                multiline
                rows={4}
                value={formData.decision_justification}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data de Início"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Data de Término Prevista"
                name="expected_end_date"
                type="date"
                value={formData.expected_end_date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <FormLabel component="legend">Prioridade</FormLabel>
                <RadioGroup
                  row
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                >
                  <FormControlLabel value="Alta" control={<Radio />} label="Alta" />
                  <FormControlLabel value="Média" control={<Radio />} label="Média" />
                  <FormControlLabel value="Baixa" control={<Radio />} label="Baixa" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <FormLabel component="legend">Pessoas Responsáveis</FormLabel>
                <Select
                  multiple
                  value={selectedResponsibles}
                  onChange={handleResponsiblesChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {technicalResponsibles.map((responsible) => (
                    <MenuItem key={responsible.responsible_id} value={responsible.full_name}>
                      <Checkbox checked={selectedResponsibles.indexOf(responsible.full_name) > -1} />
                      <ListItemText primary={responsible.full_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Valor estimado"
                name="estimated_budget"
                type="number"
                value={formData.estimated_budget}
                onChange={handleChange}
                required
                InputProps={{ 
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputProps: { min: 0, step: "0.01" }
                }}
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Formulário de Análise de Viabilidade de Desenvolvimento de Software
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', mt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 4, mb: 2 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Box sx={{ flexGrow: 1 }}></Box>
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Voltar
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" color="primary" onClick={handleNext}>
            Próximo
          </Button>
        ) : (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Finalizar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeasibilityAnalysisForm;
